import "./TaskTable.css";
import { useState, useEffect } from "react";
import useTaskTable from "./useTaskTable";
import useTaskTableColumns from "./useTaskTableColumns";
import CustomTable from "components/common/table/CustomTable";
import { DEFAULT_PAGE_SIZE_OPTIONS } from "static/pagination";
import { useGetTasksQuery } from "app/store/tasks/api/taskApi";
import TaskTableToolbar from "./TaskTableToolbar";
import { useSearchParams, useLocation } from "react-router-dom";

const TaskTable = ({
  selectedTab,
  rowSelection,
  setRowSelection,
  handleAddTask,
}) => {
  const [approvalStatus, setApprovalStatus] = useState("");

  const {
    handleRowSelection,
    handleRowClick,
    pagination,
    columnFilters,
    sorting,

    setPagination,
    setSorting,
    setColumnFilters,

    search,
    searchText,
    onSearchChange,
    columnVisibility,
  } = useTaskTable(selectedTab);
  const { data, isLoading, isFetching } = useGetTasksQuery(
    {
      selectedTab,
      query: { filters: columnFilters, pagination, search, approvalStatus },
    },
    { refetchOnMountOrArgChange: true }
  );

  const { columns, confirmDialogNode, expandedRowIds } = useTaskTableColumns(
    selectedTab,
    data?.tasks
  );

  const isArchivedOrAuditedTab = [
    "archived",
    "audited",
    "audited-archived",
    "pending-approvals",
  ].includes(selectedTab);

  const [searchParams] = useSearchParams();
  const location = useLocation();
  const highlightedDocumentId = searchParams.get("document_id");

  useEffect(() => {
    if (highlightedDocumentId) {
      setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    }
  }, [location.key]);

  return (
    <div>
      {confirmDialogNode}
      <div className="trans-table__container trans-table__container--alternate">
        <CustomTable
          columns={columns}
          data={data?.tasks || []}
          searchText={searchText}
          options={{
            enableTopToolbar: true,
            enableRowSelection: ["archived", "audited-archived"].includes(
              selectedTab
            )
              ? false
              : true,
            rowCount: data?.itemsCount || 0,
            defaultColumn: {
              size: 180,
            },
            muiSearchTextFieldProps: {
              placeholder: `Search ${["pending-approvals"].includes(selectedTab) ? "Pending" : ""} Task`,
              onPointerCancel: () => {
                onSearchChange("");
              },
            },
            state: {
              isLoading: isLoading || isFetching,
              showGlobalFilter: true,
              showColumnFilters: !["pending-approvals"].includes(selectedTab),
              globalFilter: search,
              pagination,
              columnFilters,
              sorting,
              rowSelection,
              columnVisibility,
            },
            manualPagination: true,
            manualFiltering: true,
            onPaginationChange: setPagination,
            onSortingChange: setSorting,
            onRowSelectionChange: setRowSelection,
            onColumnFiltersChange: setColumnFilters,
            onGlobalFilterChange: onSearchChange,
            rowsPerPageOptions: DEFAULT_PAGE_SIZE_OPTIONS,
            onSelectionChange: (e) => handleRowSelection(e),
            muiTableBodyCellProps: ({ row }) => ({
              sx: {
                ...(expandedRowIds.has(row.original?.id) && {
                  alignItems: "flex-start",
                }),
              },
            }),
            muiTableBodyRowProps: ({ row }) => {
              const rowData = row?.original;
              const isHighlighted =
                highlightedDocumentId && rowData?.id === highlightedDocumentId;
              const isEvenRow = row.index % 2 === 0;

              return {
                onClick: () => {
                  if (!isArchivedOrAuditedTab) {
                    handleRowClick(rowData);
                  }
                },
                sx: {
                  cursor: !isArchivedOrAuditedTab ? "pointer" : "default",
                  ...(isHighlighted
                    ? { backgroundColor: "#F1F6E3 !important" }
                    : isEvenRow && {
                        border: "1px solid #E0E0E0",
                      }),
                },
              };
            },
            renderTopToolbar: ({ table }) => (
              <TaskTableToolbar
                table={table}
                sorting={sorting}
                filters={columnFilters}
                handleAddTask={handleAddTask}
                selectedTab={selectedTab}
                approvalStatus={approvalStatus}
                onApprovalStatusChange={setApprovalStatus}
              />
            ),
          }}
        />
      </div>
    </div>
  );
};

export default TaskTable;
